/**
 * AnnouncementEditorModal Component
 * 
 * Modal for editing announcement content. Allows admin to modify the current
 * announcement configuration with a simple form interface.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Save, Eye, Calendar } from 'lucide-react';
import { Button } from './button';
import { currentAnnouncement, type Announcement, type AnnouncementItem } from '../../config/announcements';
import { announcementConfigManager } from '../../lib/announcement-config-manager';

interface AnnouncementEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (announcement: Announcement) => void;
}

export const AnnouncementEditorModal: React.FC<AnnouncementEditorModalProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  const [editedAnnouncement, setEditedAnnouncement] = useState<Announcement>(announcementConfigManager.getCurrentConfig());
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setEditedAnnouncement({ ...announcementConfigManager.getCurrentConfig() });
      setIsPreviewMode(false);
    }
  }, [isOpen]);

  // Subscribe to configuration changes to keep modal in sync
  useEffect(() => {
    if (isOpen) {
      const unsubscribe = announcementConfigManager.subscribe((newConfig) => {
        // Only update if we're not in the middle of editing
        if (!isPreviewMode) {
          setEditedAnnouncement({ ...newConfig });
        }
      });

      return unsubscribe;
    }
  }, [isOpen, isPreviewMode]);

  // Close on escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen, onClose]);

  const handleSave = () => {
    const updatedAnnouncement = {
      ...editedAnnouncement,
      updatedAt: new Date().toISOString()
    };
    onSave(updatedAnnouncement);
    onClose();
  };

  const addNewItem = () => {
    const newItem: AnnouncementItem = {
      id: `item-${Date.now()}`,
      text: '',
      icon: '✨'
    };
    setEditedAnnouncement(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const updateItem = (itemId: string, field: keyof AnnouncementItem, value: string) => {
    setEditedAnnouncement(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeItem = (itemId: string) => {
    setEditedAnnouncement(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center">
                <span className="text-white text-lg">📢</span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {isPreviewMode ? 'Preview Announcement' : 'Edit Announcement'}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {isPreviewMode ? 'How users will see this announcement' : 'Modify the current announcement content'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsPreviewMode(!isPreviewMode)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <Eye size={16} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X size={16} />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {isPreviewMode ? (
              /* Preview Mode */
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 p-4 rounded-xl border border-red-200 dark:border-red-800/30">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✨</span>
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                      {editedAnnouncement.title}
                    </h3>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="inline-block px-2 py-1 bg-white/60 dark:bg-gray-800/60 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-full">
                      {editedAnnouncement.version}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  {editedAnnouncement.items.map((item, index) => (
                    <div key={item.id} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                      <div className="w-8 h-8 bg-white dark:bg-gray-700 rounded-lg shadow-sm flex items-center justify-center flex-shrink-0">
                        <span className="text-lg">{item.icon || '✨'}</span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                        {item.text || 'Empty item'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Edit Mode */
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      value={editedAnnouncement.title}
                      onChange={(e) => setEditedAnnouncement(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="What's New in v1.02"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Version
                    </label>
                    <input
                      type="text"
                      value={editedAnnouncement.version}
                      onChange={(e) => setEditedAnnouncement(prev => ({ ...prev, version: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="v1.02"
                    />
                  </div>
                </div>

                {/* Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Auto-hide Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={editedAnnouncement.showUntil?.split('T')[0] || ''}
                      onChange={(e) => setEditedAnnouncement(prev => ({ 
                        ...prev, 
                        showUntil: e.target.value || undefined 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <select
                      value={editedAnnouncement.isActive ? 'active' : 'inactive'}
                      onChange={(e) => setEditedAnnouncement(prev => ({ 
                        ...prev, 
                        isActive: e.target.value === 'active' 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                {/* Announcement Items */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Announcement Items
                    </label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addNewItem}
                      className="flex items-center gap-2"
                    >
                      <Plus size={14} />
                      Add Item
                    </Button>
                  </div>
                  
                  <div className="space-y-3">
                    {editedAnnouncement.items.map((item, index) => (
                      <div key={item.id} className="flex items-start gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                        <input
                          type="text"
                          value={item.icon}
                          onChange={(e) => updateItem(item.id, 'icon', e.target.value)}
                          className="w-12 px-2 py-1 text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          placeholder="🎉"
                        />
                        <input
                          type="text"
                          value={item.text}
                          onChange={(e) => updateItem(item.id, 'text', e.target.value)}
                          className="flex-1 px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="Describe what's new..."
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(item.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  {editedAnnouncement.items.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Calendar size={24} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No announcement items yet</p>
                      <p className="text-xs">Click "Add Item" to get started</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Changes take effect immediately for all users
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              {!isPreviewMode && (
                <Button onClick={handleSave} className="bg-red-600 hover:bg-red-700 text-white">
                  <Save size={16} className="mr-2" />
                  Save Changes
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AnnouncementEditorModal;